import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  EnvelopeSimple, Globe, Sparkle, CheckCircle, ArrowRight,
  ArrowsCounterClockwise, Info, ShoppingCart, ArrowLeft, Rocket,
  MagnifyingGlass, ArrowClockwise, XCircle,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

/**
 * Iter208.18 → Iter208.64 — My Domain wizard.
 *
 * Step: plans → domain → summary → (site-wizard | done).
 * - Progress persistito su backend (`/me/my-domain-progress`) così il pro
 *   può riprendere da dove aveva lasciato dopo qualche giorno.
 * - Step "summary" mostra riepilogo carrello con prezzo mensile/annuale
 *   (sconto -15% annuale sul piano + prezzo dominio annuale se acquisto).
 * - Step "buy domain" ha una ricerca reale (MOCK backend) con lista TLD
 *   e bottone "Acquista" che crea l'ordine.
 * - Modalità test UX: nessun blocco su dati non validi.
 */
const PLANS = [
  {
    key: "email", title: "Email professionale",
    tagline: "Crea la tua casella email con dominio personalizzato",
    monthly: 9.90,
    badge: { label: "Email inclusa", tone: "purple" },
    icon: EnvelopeSimple,
    features: [
      "Casella email @nomeagenzia.it",
      "Fino a 5 caselle utente",
      "Antispam professionale",
      "Configurazione guidata",
    ],
    gradient: "from-purple-500 to-fuchsia-500",
    isRecommended: false, needsSite: false,
  },
  {
    key: "site", title: "Sito web",
    tagline: "Crea il tuo sito immobiliare con dominio personalizzato",
    monthly: 19.90,
    badge: { label: "Sito incluso", tone: "emerald" },
    icon: Globe,
    features: [
      "Sito responsive personalizzato",
      "Dominio .it o .com incluso",
      "Vetrina immobili automatica",
      "SEO base + Google Analytics",
    ],
    gradient: "from-emerald-500 to-teal-500",
    isRecommended: false, needsSite: true,
  },
  {
    key: "all", title: "Tutto incluso",
    tagline: "Sito web + email professionale",
    monthly: 24.90,
    badge: { label: "Più conveniente", tone: "amber" },
    icon: Sparkle,
    features: [
      "Tutto del piano Email",
      "Tutto del piano Sito web",
      "Sconto 15% rispetto ai singoli piani",
      "Priority support",
    ],
    gradient: "from-purple-600 via-fuchsia-500 to-amber-500",
    isRecommended: true, needsSite: true,
  },
];

const BADGE_TONES = {
  purple: "bg-purple-100 text-purple-800 border-purple-200",
  emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
  amber: "bg-amber-100 text-amber-900 border-amber-300",
};

const ANNUAL_DISCOUNT = 0.15; // -15%
const fmtEur = (v) => `€ ${(Math.round(v * 100) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function MyDomain() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Iter209q — Piano attivo del pro (subscription_plan). Serve a marcare
  // la card corrispondente con badge "Piano attivo" e a cambiare la CTA.
  const { user } = useAuth();
  const currentPlanId = user?.subscription_plan || null;

  const [step, setStep] = useState("plans");
  const [selectedPlan, setSelectedPlan] = useState(null);
  // Iter208.64 — Dominio scelto: { source: "buy"|"transfer", fqdn, tld?, order_id? }
  const [domainChoice, setDomainChoice] = useState(null);
  const [billingCycle, setBillingCycle] = useState("annual");

  // Iter208.64 — Resume progress
  const [savedProgress, setSavedProgress] = useState(null);
  const [progressLoaded, setProgressLoaded] = useState(false);

  // Iter209p — Stato produzione (sito/email attivi) per decidere quale
  // dialog di conferma mostrare al click su una card piano.
  const [productionState, setProductionState] = useState({
    site_in_production: false,
    email_in_production: false,
    has_progress: false,
  });
  // Iter209p — Piano correntemente in progress in DB (usato per il testo
  // dinamico dello Scenario B). Popolato dal GET iniziale del progresso
  // e aggiornato dopo ogni `saveProgress({ plan })`.
  const [currentPlanInProgress, setCurrentPlanInProgress] = useState(null);
  // Dialog conferma cambio piano: `null` = nascosto, altrimenti `{ planKey, scenario }`
  // scenario: "cancel-wizard" (Scenario B) | "already-live" (Scenario C/D)
  const [planConfirm, setPlanConfirm] = useState(null);
  const [planSwitching, setPlanSwitching] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/me/my-domain-progress");
        const p = r.data?.progress || {};
        if (p.plan) {
          setSavedProgress(p);
          setCurrentPlanInProgress(p.plan);
        }
      } catch (_e) { /* no-op: modalità test UX */ }
      finally { setProgressLoaded(true); }
    })();
    (async () => {
      try {
        const r = await api.get("/me/production-state");
        setProductionState({
          site_in_production: !!r.data?.site_in_production,
          email_in_production: !!r.data?.email_in_production,
          has_progress: !!r.data?.has_progress,
        });
      } catch (_e) { /* silent: fallback = tutto false, scenario A */ }
    })();
  }, []);

  // Iter208.65 — Deep-link `?resume=1` (arriva dall'email di onboarding):
  // appena il progresso è caricato, riprende automaticamente senza far
  // vedere il banner di scelta.
  // Iter209ag — Auto-start dal cambio piano: se arriviamo da
  // `/subscription-onboarding` con `?plan=X&stage=domain&auto=1`,
  // saltiamo la schermata di scelta piano e apriamo direttamente lo
  // step dominio. Il pro ha già scelto il piano, non deve rifarlo.
  useEffect(() => {
    if (searchParams.get("auto") !== "1") return;
    const p = (searchParams.get("plan") || "").toLowerCase();
    if (!["email", "site", "all"].includes(p)) return;
    setSelectedPlan(p);
    setProductionState((s) => ({ ...s, has_progress: true }));
    setCurrentPlanInProgress(p);
    saveProgress({ plan: p });
    const stage = searchParams.get("stage");
    if (stage === "domain" || !stage) setStep("domain");
    else setStep(stage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!progressLoaded) return;
    if (searchParams.get("resume") !== "1") return;
    if (!savedProgress) return;
    // usiamo direttamente la stessa logica di resumeProgress
    setSelectedPlan(savedProgress.plan || null);
    if (savedProgress.billing_cycle) setBillingCycle(savedProgress.billing_cycle);
    if (savedProgress.domain_value) {
      setDomainChoice({ source: savedProgress.domain_choice || "buy", fqdn: savedProgress.domain_value });
      setStep("summary");
    } else {
      setStep("domain");
    }
    setSavedProgress(null);
    toast.info("Riprendiamo da dove eri rimasto.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressLoaded]);

  const saveProgress = async (patch) => {
    try {
      await api.put("/me/my-domain-progress", patch);
      // Iter209p — Marca "wizard in corso" nello stato locale così il
      // successivo click sulle card riconosce lo Scenario B senza dover
      // rifetchare production-state ogni volta.
      if (patch && patch.plan) {
        setProductionState((s) => ({ ...s, has_progress: true }));
        setCurrentPlanInProgress(patch.plan);
      }
    } catch (_e) { /* silent */ }
  };

  const resumeProgress = () => {
    if (!savedProgress) return;
    setSelectedPlan(savedProgress.plan || null);
    if (savedProgress.billing_cycle) setBillingCycle(savedProgress.billing_cycle);
    if (savedProgress.domain_value) {
      setDomainChoice({ source: savedProgress.domain_choice || "buy", fqdn: savedProgress.domain_value });
      setStep("summary");
    } else {
      setStep("domain");
    }
    setSavedProgress(null);
  };

  const discardProgress = async () => {
    setSavedProgress(null);
    setCurrentPlanInProgress(null);
    setProductionState((s) => ({ ...s, has_progress: false }));
    try { await api.delete("/me/my-domain-progress"); } catch (_e) { /* silent */ }
  };

  // Iter208.37 — Trasferimento dominio esistente (modal)
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({
    domain: "", epp_code: "", use_for: "both", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Iter208.44 — chat AI
  const [chatMessages, setChatMessages] = useState([
    { role: "assistant", body: "Ciao! Per aiutarti a trasferire il tuo dominio, dimmi: qual è il provider dove è attualmente registrato? (es. Aruba, GoDaddy, Register.it, Namecheap...)" },
  ]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatDone, setChatDone] = useState(false);

  const sendProviderQuery = async () => {
    const provider = chatInput.trim();
    if (!provider || chatLoading || chatDone) return;
    setChatMessages((m) => [...m, { role: "user", body: provider }]);
    setChatInput("");
    setChatLoading(true);
    try {
      const r = await api.post("/me/domain-transfer/chat", { provider });
      const instructions = r.data?.instructions || "";
      setChatMessages((m) => [...m, { role: "assistant", body: instructions }]);
      const em = await api.post("/me/domain-transfer/send-instructions", { provider, instructions });
      const emailNote = em.data?.email_sent
        ? "✅ Ti ho mandato questo riepilogo anche via email."
        : "ℹ️ Servizio email momentaneamente non disponibile: puoi copiare le istruzioni qui.";
      setChatMessages((m) => [...m, { role: "assistant", body: emailNote }]);
      setChatDone(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Servizio momentaneamente non disponibile";
      toast.warning(`Assistente non raggiungibile: ${detail}. Puoi procedere manualmente.`);
      setChatMessages((m) => [...m, {
        role: "assistant",
        body: "⚠️ Non ho recuperato le istruzioni ora. Contatta il tuo attuale provider per ottenere il codice EPP/Auth code e prosegui con il form qui sotto.",
        error: true,
      }]);
      setChatDone(true);
    } finally {
      setChatLoading(false);
    }
  };

  const resetTransferChat = () => {
    setChatMessages([{ role: "assistant", body: "Ciao! Per aiutarti a trasferire il tuo dominio, dimmi: qual è il provider dove è attualmente registrato? (es. Aruba, GoDaddy, Register.it, Namecheap...)" }]);
    setChatInput(""); setChatDone(false); setChatLoading(false);
  };

  const setField = (k, v) => setTransferForm((f) => ({ ...f, [k]: v }));

  const submitTransfer = async () => {
    const d = (transferForm.domain || "").trim().toLowerCase();
    if (!d || !d.includes(".")) {
      toast.warning("Dominio inserito non valido: procediamo comunque (modalità test).");
    }
    setSubmitting(true);
    try {
      await api.post("/me/domain-transfer", {
        domain: d || "esempio.it",
        epp_code: transferForm.epp_code.trim() || null,
        use_for: transferForm.use_for,
        notes: transferForm.notes.trim() || null,
      });
      toast.success("Richiesta di trasferimento inviata.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "servizio momentaneamente non disponibile";
      toast.warning(`Richiesta non registrata (${detail}). Proseguo comunque.`);
    } finally {
      setSubmitting(false);
      setTransferOpen(false);
      const fqdn = d || "esempio.it";
      setDomainChoice({ source: "transfer", fqdn });
      await saveProgress({ domain_choice: "transfer", domain_value: fqdn });
      setStep("summary");
      setTransferForm({ domain: "", epp_code: "", use_for: "both", notes: "" });
    }
  };

  // ---- Buy domain modal (Iter208.64) ----
  const [buyOpen, setBuyOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [purchasing, setPurchasing] = useState(null); // fqdn in fase di purchase

  const openBuyModal = () => {
    setBuyOpen(true);
    setSearchQuery("");
    setSearchResults([]);
  };

  // Iter209k — Sanitizzazione input di ricerca dominio.
  // Rimuove: protocol, prefissi www*/www, path/query, TLD finale.
  // Esempi:
  //   "www.housetag.it" → "housetag"
  //   "https://www2.housetag.com/path?x=1" → "housetag"
  //   "housetag.co.uk" → "housetag"
  //   " Housetag.Agency " → "housetag"
  const sanitizeDomainQuery = (raw) => {
    if (!raw) return "";
    let s = String(raw).trim().toLowerCase();
    // rimuove protocollo
    s = s.replace(/^https?:\/\//, "");
    // rimuove qualsiasi variante di prefisso "www" (www., www1., wwwx.)
    s = s.replace(/^www[a-z0-9-]*\./, "");
    // rimuove path/query/fragment
    s = s.split("/")[0].split("?")[0].split("#")[0];
    // strip TLD finali ricorsivamente (es. .co.uk, .com.br, .agency)
    // Ripete lo strip finché resta almeno un "." e la parte finale
    // sembra un TLD (2-10 chars alfabetici).
    while (/\.[a-z]{2,10}$/i.test(s)) {
      s = s.replace(/\.[a-z]{2,10}$/i, "");
    }
    return s.trim();
  };

  const runSearch = async () => {
    const q = sanitizeDomainQuery(searchQuery);
    if (!q) {
      toast.warning("Inserisci un nome da cercare (es. tuostudio).");
      return;
    }
    // Riporta nel campo il valore sanitizzato così l'utente vede su cosa
    // è stata effettivamente lanciata la ricerca.
    if (q !== (searchQuery || "").trim().toLowerCase()) {
      setSearchQuery(q);
    }
    setSearching(true);
    try {
      const r = await api.post("/me/domain-search", { query: q });
      setSearchResults(r.data?.results || []);
      if ((r.data?.results || []).length === 0) {
        toast.info(r.data?.hint || "Nessun risultato.");
      }
    } catch (e) {
      toast.warning("Ricerca non disponibile ora, riprova o prosegui in modalità demo.");
    } finally {
      setSearching(false);
    }
  };

  const purchaseDomain = async (result) => {
    setPurchasing(result.fqdn);
    try {
      const r = await api.post("/me/domain-orders", {
        domain: result.fqdn,
        tld: result.tld,
        billing_cycle: billingCycle,
      });
      const order = r.data?.order;
      toast.success(`Ordine per ${result.fqdn} registrato. Ti contatteremo per confermare.`);
      setBuyOpen(false);
      setDomainChoice({ source: "buy", fqdn: result.fqdn, tld: result.tld, order_id: order?._id, price: order?.price_eur });
      await saveProgress({ domain_choice: "buy", domain_value: result.fqdn, billing_cycle: billingCycle });
      setStep("summary");
    } catch (e) {
      toast.warning("Ordine non registrato ora, ma proseguo in modalità demo.");
      setBuyOpen(false);
      setDomainChoice({ source: "buy", fqdn: result.fqdn, tld: result.tld });
      setStep("summary");
    } finally {
      setPurchasing(null);
    }
  };

  // ---- Step transitions ----
  const startFromPlan = async (planKey) => {
    setSelectedPlan(planKey);
    await saveProgress({ plan: planKey });
    setStep("domain");
  };

  // Iter209p — Handler unico del click sulle card piano. Decide se
  // procedere direttamente (Scenario A), chiedere conferma di cancellare
  // il wizard in corso (Scenario B) oppure chiedere conferma di
  // sostituire il piano lasciando intatta la produzione (Scenario C/D).
  const handlePlanClick = (planKey) => {
    // Iter209q — Se è già il piano attivo del pro, no-op (nessun dialog).
    if (currentPlanId && currentPlanId === planKey) return;
    const { site_in_production, email_in_production, has_progress } = productionState;
    const hasProduction = site_in_production || email_in_production;
    const hasWizardInProgress = !!savedProgress || has_progress;
    if (hasProduction) {
      // Scenario C (o D — priorità produzione): sito/email restano online.
      setPlanConfirm({ planKey, scenario: "already-live" });
      return;
    }
    if (hasWizardInProgress) {
      // Scenario B: c'è un wizard iniziato ma niente in produzione.
      setPlanConfirm({ planKey, scenario: "cancel-wizard" });
      return;
    }
    // Scenario A: nulla in corso, nulla in produzione → procedi direttamente.
    startFromPlan(planKey);
  };

  // Iter209p — Conferma Scenario B: cancella progresso + ordini pending,
  // poi ricomincia il wizard con il nuovo piano.
  const confirmCancelWizard = async () => {
    if (!planConfirm) return;
    setPlanSwitching(true);
    try {
      await api.delete("/me/my-domain-progress");
      // reset stato locale coerente con quello che appena cancellato in DB
      setSavedProgress(null);
      setCurrentPlanInProgress(null);
      setProductionState((s) => ({ ...s, has_progress: false }));
      setDomainChoice(null);
      const planKey = planConfirm.planKey;
      setPlanConfirm(null);
      await startFromPlan(planKey);
      toast.success("Ricominciamo con il nuovo piano.");
    } catch (_e) {
      toast.warning("Non ho potuto azzerare il vecchio piano. Procedo comunque.");
      const planKey = planConfirm.planKey;
      setPlanConfirm(null);
      await startFromPlan(planKey);
    } finally {
      setPlanSwitching(false);
    }
  };

  // Iter209p — Conferma Scenario C/D: sito/email restano online, aggiorna
  // solo il piano dell'utente senza toccare site_config o domain_orders.
  const confirmSwitchPlanKeepingProduction = async () => {
    if (!planConfirm) return;
    setPlanSwitching(true);
    try {
      await api.put("/users/me/subscription", { plan_id: planConfirm.planKey });
      toast.success("Piano aggiornato. Il tuo sito/casella restano attivi.");
      setPlanConfirm(null);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Riprova più tardi.";
      toast.warning(`Non ho potuto aggiornare il piano: ${detail}`);
    } finally {
      setPlanSwitching(false);
    }
  };

  // Testi dinamici del dialog Scenario B (cancellazione wizard).
  const cancelWizardMessage = useMemo(() => {
    // Il piano "in corso" può venire:
    //  - dal progresso salvato al mount (`savedProgress?.plan`)
    //  - dallo state di sessione `currentPlanInProgress`
    //    (aggiornato ad ogni saveProgress in questa sessione)
    //  - dal piano appena scelto (`selectedPlan`) come fallback.
    const s = savedProgress?.plan || currentPlanInProgress || selectedPlan;
    // "site" e "all" includono il sito; "email" e "all" includono l'email.
    const hasSite = s === "site" || s === "all";
    const hasEmail = s === "email" || s === "all";
    if (hasSite && hasEmail) {
      return "Scegliendo un nuovo piano cancellerai il lavoro fatto fino ad ora per creare il tuo sito web e la tua casella email.";
    }
    if (hasSite) {
      return "Scegliendo un nuovo piano cancellerai il lavoro fatto fino ad ora per creare il tuo sito web.";
    }
    if (hasEmail) {
      return "Scegliendo un nuovo piano cancellerai il lavoro fatto fino ad ora per configurare la tua casella email.";
    }
    // fallback prudente
    return "Scegliendo un nuovo piano cancellerai il lavoro fatto fino ad ora.";
  }, [savedProgress, currentPlanInProgress, selectedPlan]);

  // Testi dinamici del dialog Scenario C/D (produzione attiva).
  const alreadyLiveMessage = useMemo(() => {
    const { site_in_production, email_in_production } = productionState;
    if (site_in_production && email_in_production) {
      return "Il tuo sito web e la tua casella email resteranno attivi. Aggiornando il piano potranno cambiare le funzionalità disponibili.";
    }
    if (site_in_production) {
      return "Il tuo sito web resterà online. Aggiornando il piano potranno cambiare le funzionalità disponibili.";
    }
    if (email_in_production) {
      return "La tua casella email resterà attiva. Aggiornando il piano potranno cambiare le funzionalità disponibili.";
    }
    return "Aggiornando il piano potranno cambiare le funzionalità disponibili.";
  }, [productionState]);

  const goBackToPlans = () => {
    setStep("plans");
    setSelectedPlan(null);
    setDomainChoice(null);
  };

  const goBackToDomain = () => setStep("domain");

  const advanceFromSummary = async () => {
    await saveProgress({ billing_cycle: billingCycle });
    const plan = PLANS.find((p) => p.key === selectedPlan);
    if (plan?.needsSite) {
      // Iter208.64 — Passa il dominio scelto al wizard via query string,
      // così SitePreview può mostrare la URL nel chrome del browser mockup.
      const dom = domainChoice?.fqdn ? `?domain=${encodeURIComponent(domainChoice.fqdn)}` : "";
      toast.success("Passiamo al wizard sito.");
      navigate(`/site-wizard${dom}`);
    } else {
      toast.success("Perfetto! Ti contatteremo per completare la configurazione della casella email.");
      setStep("plans");
      setSelectedPlan(null);
      setDomainChoice(null);
    }
  };

  const activePlan = PLANS.find((p) => p.key === selectedPlan);

  // Riepilogo prezzi
  const summary = useMemo(() => {
    if (!activePlan) return null;
    const monthly = activePlan.monthly;
    const annualPerMonth = monthly * (1 - ANNUAL_DISCOUNT);
    const annualTotal = annualPerMonth * 12;
    const domainPrice = domainChoice?.source === "buy" ? (domainChoice?.price || 12.90) : 0;
    return {
      planMonthly: monthly,
      planAnnualPerMonth: annualPerMonth,
      planAnnualTotal: annualTotal,
      domainPrice,
      monthlyPay: monthly,
      annualPay: annualTotal + domainPrice,
    };
  }, [activePlan, domainChoice]);

  return (
    <div className="space-y-8" data-testid="my-domain-page">
      <header>
        <div className="text-xs font-semibold text-purple-600 uppercase tracking-wider">Abbonamenti</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2">
          Porta il tuo studio online.
        </h1>
        <p className="text-sm sm:text-base text-slate-600 max-w-2xl mt-3 leading-relaxed">
          {step === "plans"
            ? "Scegli il piano che fa per te: casella email professionale, sito immobiliare o soluzione all-in-one."
            : step === "domain"
              ? `Piano selezionato: ${activePlan?.title || ""}. Scegli come vuoi ottenere il dominio.`
              : `Riepilogo: ${activePlan?.title || ""} + ${domainChoice?.fqdn || "dominio scelto"}.`}
        </p>
        {step !== "plans" && <StepIndicator step={step} needsSite={!!activePlan?.needsSite} />}
      </header>

      {/* Resume banner */}
      {progressLoaded && savedProgress && step === "plans" && (
        <div
          className="rounded-xl border border-purple-200 bg-purple-50/70 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between"
          data-testid="my-domain-resume-banner"
        >
          <div className="flex items-start gap-3">
            <ArrowClockwise size={20} weight="bold" className="text-purple-600 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-semibold text-purple-900">Riprendi da dove avevi lasciato</div>
              <div className="text-xs text-purple-800 mt-0.5">
                Piano: <strong>{PLANS.find((p) => p.key === savedProgress.plan)?.title || savedProgress.plan}</strong>
                {savedProgress.domain_value && <> · Dominio: <strong>{savedProgress.domain_value}</strong></>}
                {savedProgress.updated_at && <> · Aggiornato: {new Date(savedProgress.updated_at).toLocaleDateString("it-IT")}</>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" onClick={discardProgress} className="text-slate-600 hover:text-red-600" data-testid="my-domain-resume-discard">
              Ricomincia
            </Button>
            <Button onClick={resumeProgress} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="my-domain-resume-btn">
              Riprendi <ArrowRight size={14} weight="bold" className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* STEP 1 — Piani */}
      {step === "plans" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6" data-testid="my-domain-plans">
          {PLANS.map((plan) => {
            const Icon = plan.icon;
            const recommended = plan.isRecommended;
            const isActive = currentPlanId === plan.key;
            return (
              <div
                key={plan.key}
                data-testid={`my-domain-plan-${plan.key}`}
                data-plan-active={isActive || undefined}
                className={
                  "relative bg-white rounded-2xl shadow-md p-5 sm:p-6 flex flex-col transition-all duration-300 ease-out will-change-transform hover:-translate-y-1.5 hover:shadow-2xl " +
                  (isActive
                    ? "ring-2 ring-emerald-500 border border-emerald-200 shadow-emerald-100"
                    : recommended
                      ? "ring-2 ring-purple-500 border border-purple-200 md:scale-[1.02]"
                      : "border border-slate-200")
                }
              >
                <div className={"absolute -top-3 left-5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border shadow-sm " + BADGE_TONES[plan.badge.tone] + (recommended ? " ring-2 ring-amber-300 animate-pulse" : "")} data-testid={`my-domain-badge-${plan.key}`}>
                  {recommended && <Sparkle size={11} weight="fill" />}
                  {plan.badge.label}
                </div>
                {/* Iter209q — Badge "Piano attivo" sulla card corrispondente al piano corrente del pro */}
                {isActive && (
                  <div
                    className="absolute -top-3 right-5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 shadow-sm"
                    data-testid={`my-domain-active-badge-${plan.key}`}
                  >
                    <CheckCircle size={11} weight="fill" className="text-emerald-600" />
                    Piano attivo
                  </div>
                )}
                <div className={"inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br text-white shadow-lg mt-2 mb-4 " + plan.gradient}>
                  <Icon size={22} weight="bold" />
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900 mb-1">{plan.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-5">{plan.tagline}</p>
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="text-3xl font-bold text-slate-900">{fmtEur(plan.monthly)}</span>
                  <span className="text-sm text-slate-500 font-medium">/ mese</span>
                </div>
                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle size={16} weight="fill" className="text-emerald-500 shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => handlePlanClick(plan.key)}
                  data-testid={`my-domain-start-${plan.key}`}
                  aria-current={isActive ? "true" : undefined}
                  className={
                    "w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-semibold text-sm transition-all " +
                    (isActive
                      ? "bg-emerald-50 border-2 border-emerald-500 text-emerald-800 cursor-default hover:bg-emerald-100"
                      : recommended
                        ? "bg-gradient-to-r from-purple-600 to-fuchsia-500 hover:from-purple-700 hover:to-fuchsia-600 text-white shadow-md hover:shadow-lg"
                        : "border-2 border-purple-500 text-purple-700 hover:bg-purple-50")
                  }
                >
                  {isActive ? (
                    <>
                      <CheckCircle size={14} weight="fill" /> Piano attivo
                    </>
                  ) : (
                    <>
                      Inizia <ArrowRight size={14} weight="bold" />
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* STEP 2 — Dominio */}
      {step === "domain" && (
        <div className="space-y-4" data-testid="my-domain-step-domain">
          <button type="button" onClick={goBackToPlans} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-purple-700 font-medium" data-testid="my-domain-back-to-plans">
            <ArrowLeft size={14} weight="bold" /> Cambia piano
          </button>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
            <button type="button" onClick={openBuyModal} data-testid="my-domain-buy-new" className="text-left bg-white rounded-2xl shadow-md p-6 flex flex-col border-2 border-slate-200 hover:border-purple-500 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 will-change-transform">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-500 text-white shadow-lg mb-4">
                <ShoppingCart size={22} weight="bold" />
              </div>
              <h3 className="font-display text-lg font-bold text-slate-900 mb-2">Acquista un nuovo dominio</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-4 flex-1">
                Cerca un dominio libero (.it, .com, .agency…) e acquistalo direttamente da HouseTag senza uscire dall'app.
                <br />
                <span className="text-slate-500 italic text-xs">Ricerca disponibilità e prezzi in tempo reale (modalità demo).</span>
              </p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-700">
                Cerca dominio <ArrowRight size={14} weight="bold" />
              </span>
            </button>
            <button type="button" onClick={() => setTransferOpen(true)} data-testid="my-domain-transfer-existing" className="text-left bg-white rounded-2xl shadow-md p-6 flex flex-col border-2 border-slate-200 hover:border-purple-500 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 will-change-transform">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-lg mb-4">
                <ArrowsCounterClockwise size={22} weight="bold" />
              </div>
              <h3 className="font-display text-lg font-bold text-slate-900 mb-2">Trasferisci un dominio esistente</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-4 flex-1">
                Hai già un dominio con un altro registrar? Portalo su HouseTag. Ti guidiamo passo passo e ci occupiamo noi del trasferimento entro 24h lavorative.
              </p>
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-700">
                Inizia trasferimento <ArrowRight size={14} weight="bold" />
              </span>
            </button>
          </div>
          {activePlan?.needsSite && (
            <div className="rounded-lg border border-purple-200 bg-purple-50/60 p-3 text-sm text-purple-900 flex items-start gap-2" data-testid="my-domain-site-notice">
              <Rocket size={16} weight="fill" className="text-purple-600 shrink-0 mt-0.5" />
              <div><strong>Prossimo step:</strong> dopo aver scelto il dominio vedrai il riepilogo e poi il wizard sito (logo, colori, template).</div>
            </div>
          )}
        </div>
      )}

      {/* STEP 3 — Riepilogo */}
      {step === "summary" && summary && (
        <div className="space-y-4" data-testid="my-domain-step-summary">
          <button type="button" onClick={goBackToDomain} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-purple-700 font-medium" data-testid="my-domain-back-to-domain">
            <ArrowLeft size={14} weight="bold" /> Cambia dominio
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Cart */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-md p-5" data-testid="my-domain-summary-cart">
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Il tuo carrello</h3>

              <div className="space-y-3">
                <div className="flex items-start justify-between gap-2 pb-3 border-b border-slate-100">
                  <div className="flex items-start gap-3">
                    <div className={"shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br text-white " + activePlan.gradient}>
                      {(() => { const Icon = activePlan.icon; return <Icon size={18} weight="bold" />; })()}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{activePlan.title}</div>
                      <div className="text-xs text-slate-500">{activePlan.tagline}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-slate-900" data-testid="my-domain-summary-plan-price">
                      {fmtEur(billingCycle === "annual" ? summary.planAnnualPerMonth : summary.planMonthly)}<span className="text-xs text-slate-500 font-normal"> / mese</span>
                    </div>
                    {billingCycle === "annual" && (
                      <div className="text-[10px] font-semibold text-emerald-600 uppercase">-15% annuale</div>
                    )}
                  </div>
                </div>

                <div className="flex items-start justify-between gap-2 pb-3 border-b border-slate-100">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 text-slate-700">
                      <Globe size={18} weight="bold" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900 break-all" data-testid="my-domain-summary-domain">
                        {domainChoice?.fqdn || "Dominio da confermare"}
                      </div>
                      <div className="text-xs text-slate-500">
                        {domainChoice?.source === "buy" ? "Nuovo acquisto (modalità demo)" : "Trasferimento in gestione"}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-bold text-slate-900">
                      {domainChoice?.source === "buy" ? fmtEur(summary.domainPrice) : "Incluso"}
                    </div>
                    {domainChoice?.source === "buy" && (
                      <div className="text-[10px] text-slate-500 uppercase">1° anno</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Toggle billing cycle */}
              <div className="mt-4">
                <div className="text-xs font-semibold text-slate-700 mb-2">Ciclo di fatturazione</div>
                <div className="grid grid-cols-2 gap-2" role="tablist" data-testid="my-domain-summary-billing-toggle">
                  {[
                    { key: "monthly", label: "Mensile", desc: "Paghi ogni mese", suffix: null },
                    { key: "annual", label: "Annuale", desc: "Un pagamento all'anno", suffix: "-15%" },
                  ].map((opt) => {
                    const active = billingCycle === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => { setBillingCycle(opt.key); saveProgress({ billing_cycle: opt.key }); }}
                        className={"relative text-left rounded-lg border-2 px-3 py-2 transition-all " + (active ? "border-purple-500 bg-purple-50 shadow-sm" : "border-slate-200 hover:border-purple-300")}
                        data-testid={`my-domain-summary-billing-${opt.key}`}
                      >
                        <div className="flex items-center gap-1.5">
                          <span className={"text-sm font-semibold " + (active ? "text-purple-800" : "text-slate-800")}>{opt.label}</span>
                          {opt.suffix && (
                            <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                              <Sparkle size={9} weight="fill" />
                              {opt.suffix}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{opt.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Totale + CTA */}
            <div className="bg-gradient-to-br from-purple-50 to-white rounded-2xl border border-purple-200 shadow-md p-5 flex flex-col">
              <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Totale</h3>
              <div className="space-y-1 mb-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-slate-600">Piano ({billingCycle === "annual" ? "annuale" : "mensile"})</span>
                  <span className="text-sm font-semibold text-slate-900">
                    {billingCycle === "annual" ? fmtEur(summary.planAnnualTotal) : fmtEur(summary.planMonthly)}
                  </span>
                </div>
                {domainChoice?.source === "buy" && (
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-slate-600">Dominio (1° anno)</span>
                    <span className="text-sm font-semibold text-slate-900">{fmtEur(summary.domainPrice)}</span>
                  </div>
                )}
                <div className="flex items-baseline justify-between pt-2 mt-2 border-t border-purple-200">
                  <span className="text-base font-bold text-slate-900">Da pagare oggi</span>
                  <span className="text-2xl font-bold text-purple-700" data-testid="my-domain-summary-total">
                    {fmtEur(billingCycle === "annual" ? summary.annualPay : summary.monthlyPay)}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 italic mt-1">
                  ⚠️ Modalità demo: nessun pagamento reale viene addebitato.
                </div>
              </div>
              <Button
                onClick={advanceFromSummary}
                className="bg-gradient-to-r from-purple-600 to-fuchsia-500 hover:from-purple-700 hover:to-fuchsia-600 text-white shadow-md mt-auto"
                data-testid="my-domain-summary-continue"
              >
                {activePlan?.needsSite ? "Prosegui al wizard sito" : "Conferma piano"} <ArrowRight size={14} weight="bold" className="ml-1" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="text-center text-xs text-slate-500 pt-4 border-t border-slate-100">
        Tutti i piani includono fatturazione mensile, senza vincoli. Disdici quando vuoi.
      </div>

      {/* Iter209p — Dialog Scenario B: c'è wizard in corso, niente in produzione */}
      <Dialog
        open={!!planConfirm && planConfirm.scenario === "cancel-wizard"}
        onOpenChange={(o) => { if (!o && !planSwitching) setPlanConfirm(null); }}
      >
        <DialogContent className="max-w-md" data-testid="my-domain-plan-confirm-cancel">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <ArrowsCounterClockwise size={20} className="text-red-600" weight="bold" />
              Sei sicuro di voler cambiare piano?
            </DialogTitle>
            <DialogDescription className="text-left leading-relaxed text-slate-700" data-testid="my-domain-plan-confirm-cancel-msg">
              {cancelWizardMessage}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-end gap-2 pt-2 border-t mt-2">
            <Button
              variant="ghost"
              onClick={() => setPlanConfirm(null)}
              disabled={planSwitching}
              data-testid="my-domain-plan-confirm-cancel-abort"
            >
              Annulla
            </Button>
            <Button
              onClick={confirmCancelWizard}
              disabled={planSwitching}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="my-domain-plan-confirm-cancel-confirm"
            >
              {planSwitching ? "Attendere…" : "Sì, cambia piano"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter209p — Dialog Scenario C/D: sito o email in produzione */}
      <Dialog
        open={!!planConfirm && planConfirm.scenario === "already-live"}
        onOpenChange={(o) => { if (!o && !planSwitching) setPlanConfirm(null); }}
      >
        <DialogContent className="max-w-md" data-testid="my-domain-plan-confirm-live">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900">
              <Rocket size={20} className="text-purple-600" weight="fill" />
              Vuoi cambiare piano?
            </DialogTitle>
            <DialogDescription className="text-left leading-relaxed text-slate-700" data-testid="my-domain-plan-confirm-live-msg">
              {alreadyLiveMessage}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-end gap-2 pt-2 border-t mt-2">
            <Button
              variant="ghost"
              onClick={() => setPlanConfirm(null)}
              disabled={planSwitching}
              data-testid="my-domain-plan-confirm-live-abort"
            >
              Annulla
            </Button>
            <Button
              onClick={confirmSwitchPlanKeepingProduction}
              disabled={planSwitching}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="my-domain-plan-confirm-live-confirm"
            >
              {planSwitching ? "Attendere…" : "Sì, cambia piano"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal trasferimento */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="my-domain-transfer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowsCounterClockwise size={20} className="text-purple-600" weight="bold" />
              Trasferisci un dominio esistente
            </DialogTitle>
            <DialogDescription className="text-left leading-relaxed">
              Porta il tuo dominio (registrato altrove) su HouseTag: il team amministrativo si occupa del trasferimento e ti ricontatta entro 24 ore lavorative.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="my-domain-transfer-instructions">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Info size={14} weight="fill" className="text-purple-600" />
                Assistente trasferimento
              </div>
              {chatDone && (
                <button type="button" onClick={resetTransferChat} className="text-[11px] text-purple-700 hover:text-purple-900 font-medium" data-testid="my-domain-transfer-chat-reset">Ricomincia</button>
              )}
            </div>
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1" data-testid="my-domain-transfer-chat">
              {chatMessages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={"inline-block max-w-[85%] rounded-lg px-3 py-2 text-[12px] leading-relaxed " + (m.role === "user" ? "bg-purple-600 text-white whitespace-pre-wrap" : m.error ? "bg-red-50 border border-red-200 text-red-800 whitespace-pre-wrap" : "bg-white border border-slate-200 text-slate-800")} data-testid={`my-domain-chat-msg-${m.role}`}>
                    {m.role === "assistant" && !m.error ? (
                      <div className="prose prose-sm max-w-none prose-slate prose-p:my-1.5 prose-headings:mb-1.5 prose-headings:mt-2 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-strong:text-slate-900 prose-strong:font-semibold prose-headings:text-slate-900 prose-a:text-purple-700">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.body || ""}</ReactMarkdown>
                      </div>
                    ) : (m.body)}
                  </div>
                </div>
              ))}
              {chatLoading && (<div className="flex justify-start"><div className="inline-block bg-white border border-slate-200 rounded-lg px-3 py-2 text-[12px] italic text-slate-500">Sto cercando le istruzioni…</div></div>)}
            </div>
            {!chatDone && (
              <div className="mt-2 flex items-center gap-1">
                <Input type="text" placeholder="Scrivi il nome del provider…" value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendProviderQuery(); } }} disabled={chatLoading} className="h-8 text-xs" data-testid="my-domain-transfer-chat-input" />
                <Button type="button" onClick={sendProviderQuery} disabled={chatLoading || !chatInput.trim()} size="sm" className="h-8 px-3 bg-purple-600 hover:bg-purple-700 text-white text-xs" data-testid="my-domain-transfer-chat-send">Invia</Button>
              </div>
            )}
          </div>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Dominio da trasferire <span className="text-slate-400 font-normal">(consigliato)</span></label>
              <Input type="text" placeholder="tuostudio.it" value={transferForm.domain} onChange={(e) => setField("domain", e.target.value)} data-testid="my-domain-transfer-domain" autoFocus />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Codice EPP / Auth Code <span className="text-slate-400 font-normal">(opzionale)</span></label>
              <Input type="text" placeholder="ABCD-1234-EFGH" value={transferForm.epp_code} onChange={(e) => setField("epp_code", e.target.value)} data-testid="my-domain-transfer-epp" />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-2">Come vuoi usare il dominio?</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { v: "site", label: "Sito web", icon: Globe, desc: "Usa il dominio per il tuo sito immobiliare" },
                  { v: "email", label: "Email", icon: EnvelopeSimple, desc: "Solo caselle email professionali" },
                  { v: "both", label: "Entrambi", icon: Sparkle, desc: "Sito web + email sullo stesso dominio" },
                ].map(({ v, label, icon: Icon, desc }) => {
                  const active = transferForm.use_for === v;
                  return (
                    <button key={v} type="button" onClick={() => setField("use_for", v)} data-testid={`my-domain-transfer-use-${v}`} className={"text-left rounded-lg border-2 px-3 py-2 transition-all " + (active ? "border-purple-500 bg-purple-50 shadow-sm" : "border-slate-200 hover:border-purple-300 hover:bg-slate-50")}>
                      <div className="flex items-center gap-1.5">
                        <Icon size={14} className={active ? "text-purple-600" : "text-slate-500"} weight={active ? "fill" : "regular"} />
                        <span className={"text-xs font-semibold " + (active ? "text-purple-800" : "text-slate-700")}>{label}</span>
                        {active && <CheckCircle size={12} weight="fill" className="ml-auto text-purple-600" />}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1 leading-snug">{desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Note aggiuntive <span className="text-slate-400 font-normal">(opzionale)</span></label>
              <Textarea rows={2} placeholder="Es. registrar attuale, urgenza, preferenze DNS…" value={transferForm.notes} onChange={(e) => setField("notes", e.target.value)} data-testid="my-domain-transfer-notes" />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t mt-2">
            <Button variant="ghost" onClick={() => setTransferOpen(false)} disabled={submitting} data-testid="my-domain-transfer-cancel">Annulla</Button>
            <Button onClick={submitTransfer} disabled={submitting} className="bg-gradient-to-r from-purple-600 to-fuchsia-500 hover:from-purple-700 hover:to-fuchsia-600 text-white" data-testid="my-domain-transfer-submit">
              {submitting ? "Invio…" : (<><ArrowRight size={14} weight="bold" className="mr-1" /> Prosegui</>)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal acquisto dominio */}
      <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="my-domain-buy-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart size={20} className="text-purple-600" weight="bold" />
              Cerca e acquista un dominio
            </DialogTitle>
            <DialogDescription className="text-left leading-relaxed">
              Digita il nome che vuoi verificare (senza estensione). Ti mostriamo TLD disponibili e prezzi.
              <span className="block text-[11px] italic mt-1 text-slate-500">⚠️ Modalità demo: nessun pagamento reale viene addebitato.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-stretch gap-2">
            <Input
              type="text"
              placeholder="Es. tuostudio-immobiliare"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(); } }}
              className="flex-1"
              data-testid="my-domain-buy-search-input"
              autoFocus
            />
            <Button onClick={runSearch} disabled={searching} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="my-domain-buy-search-btn">
              {searching ? (<><ArrowClockwise size={14} className="animate-spin mr-1" />Cerco…</>) : (<><MagnifyingGlass size={14} weight="bold" className="mr-1" />Cerca</>)}
            </Button>
          </div>
          <div className="mt-3 space-y-2" data-testid="my-domain-buy-results">
            {searchResults.length === 0 && !searching && (
              <div className="text-center text-xs text-slate-500 italic py-6">
                Inserisci un nome e premi "Cerca" per vedere i domini disponibili.
              </div>
            )}
            {searchResults.map((r) => (
              <div key={r.fqdn} className={"flex items-center justify-between gap-3 rounded-lg border-2 px-3 py-2 " + (r.available ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-slate-50 opacity-70")} data-testid={`my-domain-buy-result-${r.tld.slice(1)}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
                    {r.fqdn}
                    {r.available
                      ? <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0" />
                      : <XCircle size={14} weight="fill" className="text-slate-400 shrink-0" />}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {r.available ? "Disponibile" : "Non disponibile"} · {fmtEur(r.price_annual_eur)} / anno
                  </div>
                </div>
                <Button
                  onClick={() => purchaseDomain(r)}
                  disabled={!r.available || purchasing === r.fqdn}
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 text-white disabled:bg-slate-300 disabled:text-slate-500"
                  data-testid={`my-domain-buy-purchase-${r.tld.slice(1)}`}
                >
                  {purchasing === r.fqdn ? "…" : "Acquista"}
                </Button>
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-2 border-t mt-2">
            <Button variant="ghost" onClick={() => setBuyOpen(false)} data-testid="my-domain-buy-cancel">Chiudi</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StepIndicator({ step, needsSite }) {
  const items = [
    { key: "plans", label: "Piano" },
    { key: "domain", label: "Dominio" },
  ];
  if (needsSite) items.push({ key: "summary", label: "Riepilogo" });
  if (needsSite) items.push({ key: "site", label: "Sito" });
  else items.push({ key: "summary", label: "Riepilogo" });
  const idx = items.findIndex((x) => x.key === step);
  return (
    <ol className="mt-4 flex items-center gap-3 text-xs font-semibold" data-testid="my-domain-step-indicator">
      {items.map((it, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <li key={it.key} className="inline-flex items-center gap-2">
            <span className={"inline-flex items-center justify-center w-6 h-6 rounded-full border text-[11px] " + (done ? "bg-purple-600 text-white border-purple-600" : active ? "bg-white text-purple-700 border-purple-500 ring-2 ring-purple-200" : "bg-slate-50 text-slate-400 border-slate-200")} data-testid={`my-domain-step-${it.key}`}>
              {done ? <CheckCircle size={12} weight="fill" /> : i + 1}
            </span>
            <span className={active ? "text-purple-800" : done ? "text-slate-700" : "text-slate-400"}>{it.label}</span>
            {i < items.length - 1 && <span className="w-6 h-px bg-slate-200 mx-1" />}
          </li>
        );
      })}
    </ol>
  );
}
